import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/components/ui/select";
import { Input } from "@shared/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@shared/components/ui/tabs";
import { Package, User, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import InfiniteScrollTrigger from '@shared/components/InfiniteScrollTrigger';
import ThreeDotLoader from '@shared/components/ThreeDotLoader';
import PickupCard from "@/features/pickups/components/PickupCard";
import * as pickupService from '@features/pickups/services/pickupService';
import { useAuth } from '@features/auth/contexts/AuthContext';
import { useUsersSearchQuery } from "@/features/users/useUsers";
import { Badge } from "@shared/components/ui/badge";
import { Button } from "@shared/components/ui/button";
import HeadLayout from "@shared/layouts/HeadLayout";
import {
  PICKUP_MANAGEMENT_STATUS_FILTERS,
  PICKUP_MANAGEMENT_TYPE_FILTERS,
  PICKUP_MANAGEMENT_ASSIGNMENT_FILTERS,
} from '@features/pickups/constants';

// Client Selector Component
const ClientSelector = ({ 
  selectedClient, 
  clientSearch, 
  setClientSearch, 
  clients, 
  isLoadingClients, 
  onClientSelect, 
  onClientClear, 
  t 
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      {selectedClient ? (
        <div className="flex items-center gap-2 p-2 border rounded-md bg-blue-50 dark:bg-blue-950/20">
          <User className="h-4 w-4 text-blue-600" />
          <Badge variant="secondary" className="flex items-center gap-1">
            {selectedClient.brandName || selectedClient.fullName}
            <Button
              variant="ghost"
              size="sm"
              onClick={onClientClear}
              className="h-4 w-4 p-0 ml-1 hover:bg-red-100"
            >
              <X className="h-3 w-3" />
            </Button>
          </Badge>
        </div>
      ) : (
        <div className="relative">
          <Input
            placeholder={t("pickups.searchClients")}
            value={clientSearch}
            onChange={(e) => {
              setClientSearch(e.target.value);
              setIsOpen(e.target.value.length >= 2);
            }}
            onFocus={() => setIsOpen(clientSearch.length >= 2)}
            onBlur={() => setTimeout(() => setIsOpen(false), 200)}
          />
          {isOpen && (
            <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white dark:bg-gray-800 border rounded-md shadow-lg max-h-60 overflow-y-auto">
              {isLoadingClients ? (
                <div className="p-2 text-center">
                  <ThreeDotLoader />
                </div>
              ) : clients.length > 0 ? (
                clients.map((client) => (
                  <button
                    key={client._id}
                    className="w-full text-left p-2 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                    onClick={() => {
                      onClientSelect(client);
                      setIsOpen(false);
                    }}
                  >
                    <User className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <div className="font-medium">
                        {client.brandName || client.fullName}
                      </div>
                      {client.email && (
                        <div className="text-sm text-muted-foreground">
                          {client.email}
                        </div>
                      )}
                    </div>
                  </button>
                ))
              ) : (
                <div className="p-2 text-center text-muted-foreground">
                  {t("pickups.noClientsFound")}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const PickupManagementPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [params, setParams] = useState({ limit: 10 });
  const [activeTab, setActiveTab] = useState("all");
  const [clientSearch, setClientSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);

  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "manager";
  const isLivreur = user?.role === "livreur";

  // Client search query
  const { data: clientsData, isLoading: isLoadingClients } = useUsersSearchQuery(
    clientSearch,
    10, // limit
    1, // page
    clientSearch.length >= 2, // enabled when search has at least 2 characters
    { role: "client" } // only search for clients
  );

  // Initialize filters from URL parameters
  useEffect(() => {
    const status = searchParams.get("status");
    const search = searchParams.get("search");
    const type = searchParams.get("type");
    const assignment = searchParams.get("assignment");
    const client = searchParams.get("client");

    const urlFilters = {};
    if (status) urlFilters.status = status;
    if (search) urlFilters.search = search;
    if (type) urlFilters.type = type;
    if (assignment) urlFilters.assignment = assignment;
    if (client) urlFilters.user = client;

    if (Object.keys(urlFilters).length > 0) {
      setParams((prev) => ({ ...prev, ...urlFilters, page: 1 }));
    }
  }, [searchParams]);

  // Fetch all pickups (admin) or assigned pickups (livreur)
  const {
    data: allPickupsData,
    fetchNextPage: fetchNextAllPage,
    hasNextPage: hasNextAllPage,
    isFetchingNextPage: isFetchingNextAllPage,
    isLoading: isLoadingAll,
    error: allError,
  } = useInfiniteQuery({
    queryKey: ["pickups-all", params],
    queryFn: async ({ pageParam = 1 }) => {
      const queryParams = new URLSearchParams({
        ...params,
        page: pageParam,
      });
      return pickupService.fetchAllPickups(queryParams.toString());
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage.pageInfo?.nextPage || undefined;
    },
    enabled: activeTab === "all",
  });

  // Fetch assigned pickups (livreur only)
  const {
    data: assignedPickupsData,
    fetchNextPage: fetchNextAssignedPage,
    hasNextPage: hasNextAssignedPage,
    isFetchingNextPage: isFetchingNextAssignedPage,
    isLoading: isLoadingAssigned,
    error: assignedError,
  } = useInfiniteQuery({
    queryKey: ["pickups-assigned", params],
    queryFn: async ({ pageParam = 1 }) => {
      const queryParams = new URLSearchParams({
        ...params,
        page: pageParam,
        livreur: user?.id,
      });
      return pickupService.fetchAllPickups(queryParams.toString());
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage.pageInfo?.nextPage || undefined;
    },
    enabled: activeTab === "assigned" && isLivreur,
  });

  const handleFilter = (key, value) => {
    const apiValue = value === "all" ? "" : value;
    const newParams = { ...params, [key]: apiValue, page: 1 };
    setParams(newParams);

    const newSearchParams = new URLSearchParams(searchParams);
    if (apiValue) {
      newSearchParams.set(key, apiValue);
    } else {
      newSearchParams.delete(key);
    }
    setSearchParams(newSearchParams);
  };

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setClientSearch("");
    // Use 'user' as the parameter name since that's what the API expects
    handleFilter("user", client._id);
  };

  const handleClientClear = () => {
    setSelectedClient(null);
    // Clear the 'user' filter
    handleFilter("user", "");
  };

  const clients = clientsData?.data || [];

  // Find and set selected client when user ID is in params
  useEffect(() => {
    if (params.user && !selectedClient) {
      // If we have a user ID but no selected client, we need to find the client
      // This could happen when the page loads with a user filter from URL
      const userId = params.user;
      // For now, we'll just store the ID. In a real app, you might want to fetch user details
      setSelectedClient({ _id: userId, brandName: `Client ${userId}` });
    }
  }, [params.user, selectedClient]);

  const allPickups =
    allPickupsData?.pages?.flatMap((page) => page.pickups) || [];
  const totalAllPickups = allPickupsData?.pages?.[0]?.count || 0;

  const assignedPickups =
    assignedPickupsData?.pages?.flatMap((page) => page.pickups) || [];
  const totalAssignedPickups = assignedPickupsData?.pages?.[0]?.count || 0;

  if (allError) {
    toast.error(t("Failed to load pickups"));
  }

  if (assignedError) {
    toast.error(t("Failed to load assigned pickups"));
  }

  const getStatsForPickups = (pickups) => {
    return {
      total: pickups.length,
      pending: pickups.filter((p) => p.status === "pending").length,
      approved: pickups.filter((p) => p.status === "approved").length,
      inProgress: pickups.filter((p) =>
        ["picked_up", "in_transit"].includes(p.status)
      ).length,
      completed: pickups.filter((p) => p.status === "signed").length,
      failed: pickups.filter((p) => ["rejected", "failed"].includes(p.status))
        .length,
      assigned: pickups.filter((p) => p.livreur).length,
      unassigned: pickups.filter((p) => !p.livreur).length,
    };
  };

  const renderPickupsList = (
    pickups,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage
  ) => (
    <div className="space-y-4">
      {isLoading ? (
        <div className="flex justify-center py-8">
          <ThreeDotLoader />
        </div>
      ) : pickups.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              {t("No pickups found")}
            </h3>
            <p className="text-muted-foreground">
              {params.status || params.search
                ? t("No pickups match your current filters")
                : t("No pickups available yet")}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {pickups.map((pickup) => (
              <PickupCard key={pickup._id} pickup={pickup} />
            ))}
          </div>

          {hasNextPage && (
            <div className="flex justify-center pt-4">
              <InfiniteScrollTrigger
                fetchNextPage={fetchNextPage}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <>
      <HeadLayout title={"pickup.management"} />
      <div className="p-6 space-y-6">
        {/* Main Content */}
        {(isAdmin || isManager) ? (
          // Admin/Manager View with Tabs
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="space-y-6"
          >
            <TabsList className="grid w-full grid-cols-1">
              <TabsTrigger value="all">{t("All Pickups")}</TabsTrigger>
            </TabsList>

            <TabsContent value="all" className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                {(() => {
                  const stats = getStatsForPickups(allPickups);
                  return (
                    <>
                      <Card>
                        <CardContent className="p-4">
                          <div className="text-2xl font-bold">
                            {totalAllPickups}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {t("Total Pickups")}
                          </p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="p-4">
                          <div className="text-2xl font-bold text-gray-600">
                            {stats.pending}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {t("Pending")}
                          </p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="p-4">
                          <div className="text-2xl font-bold text-green-600">
                            {stats.approved}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {t("Approved")}
                          </p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="p-4">
                          <div className="text-2xl font-bold text-yellow-600">
                            {stats.inProgress}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {t("In Progress")}
                          </p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="p-4">
                          <div className="text-2xl font-bold text-blue-600">
                            {stats.assigned}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {t("Assigned")}
                          </p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="p-4">
                          <div className="text-2xl font-bold text-orange-600">
                            {stats.unassigned}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {t("Unassigned")}
                          </p>
                        </CardContent>
                      </Card>
                    </>
                  );
                })()}
              </div>

              {/* Filters */}
              <Card>
                <CardHeader>
                  <CardTitle>{t("Filter Pickups")}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <Select
                      value={params.status || "all"}
                      onValueChange={(value) => handleFilter("status", value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t("Filter by status")} />
                      </SelectTrigger>
                      <SelectContent>
                        {PICKUP_MANAGEMENT_STATUS_FILTERS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {t(option.label)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select
                      value={params.type || "all"}
                      onValueChange={(value) => handleFilter("type", value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t("Filter by type")} />
                      </SelectTrigger>
                      <SelectContent>
                        {PICKUP_MANAGEMENT_TYPE_FILTERS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {t(option.label)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select
                      value={params.assignment || "all"}
                      onValueChange={(value) =>
                        handleFilter("assignment", value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t("Filter by assignment")} />
                      </SelectTrigger>
                      <SelectContent>
                        {ASSIGNMENT_FILTER_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {t(option.label)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <ClientSelector
                      selectedClient={selectedClient}
                      clientSearch={clientSearch}
                      setClientSearch={setClientSearch}
                      clients={clients}
                      isLoadingClients={isLoadingClients}
                      onClientSelect={handleClientSelect}
                      onClientClear={handleClientClear}
                      t={t}
                    />

                    <Input
                      placeholder={t("Search pickups...")}
                      value={params.search || ""}
                      onChange={(e) => handleFilter("search", e.target.value)}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Pickups List */}
              {renderPickupsList(
                allPickups,
                isLoadingAll,
                hasNextAllPage,
                isFetchingNextAllPage,
                fetchNextAllPage
              )}
            </TabsContent>
          </Tabs>
        ) : (
          // Livreur View
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {(() => {
                const stats = getStatsForPickups(assignedPickups);
                return (
                  <>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold">
                          {totalAssignedPickups}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {t("Assigned Pickups")}
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-yellow-600">
                          {stats.inProgress}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {t("In Progress")}
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-green-600">
                          {stats.completed}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {t("Completed")}
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-red-600">
                          {stats.failed}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {t("Failed")}
                        </p>
                      </CardContent>
                    </Card>
                  </>
                );
              })()}
            </div>

            {/* Filters */}
            <Card>
              <CardHeader>
                <CardTitle>{t("Filter My Pickups")}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Select
                    value={params.status || "all"}
                    onValueChange={(value) => handleFilter("status", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t("Filter by status")} />
                    </SelectTrigger>
                    <SelectContent>
                      {PICKUP_STATUS_FILTER_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {t(option.label)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select
                    value={params.type || "all"}
                    onValueChange={(value) => handleFilter("type", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t("Filter by type")} />
                    </SelectTrigger>
                    <SelectContent>
                      {PICKUP_TYPE_FILTER_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {t(option.label)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <ClientSelector
                    selectedClient={selectedClient}
                    clientSearch={clientSearch}
                    setClientSearch={setClientSearch}
                    clients={clients}
                    isLoadingClients={isLoadingClients}
                    onClientSelect={handleClientSelect}
                    onClientClear={handleClientClear}
                    t={t}
                  />

                  <Input
                    placeholder={t("Search pickups...")}
                    value={params.search || ""}
                    onChange={(e) => handleFilter("search", e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Assigned Pickups List */}
            {renderPickupsList(
              assignedPickups,
              isLoadingAssigned,
              hasNextAssignedPage,
              isFetchingNextAssignedPage,
              fetchNextAssignedPage
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default PickupManagementPage;
